import { useCallback, useRef, useState, useEffect } from 'react';
import { useStore } from '../../store/useStore';

// Pentatonic scale frequencies for pleasing harmonics
const PENTATONIC_SCALE = [
  130.81,
  146.83,
  164.81,
  196.0,
  220.0, // C3-A3
  261.63,
  293.66,
  329.63,
  392.0,
  440.0, // C4-A4
  523.25,
  587.33,
  659.25,
  783.99,
  880.0, // C5-A5
  1046.5,
  1174.66, // C6-D6
];

// Map 0-255 to scale index
const ruleToFrequency = (rule: number): number => {
  const index = Math.floor((rule / 255) * (PENTATONIC_SCALE.length - 1));
  return PENTATONIC_SCALE[index];
};

// Map 0-255 to a continuous frequency range
const ruleToContinuousFrequency = (rule: number): number => {
  const minFreq = 80;
  const maxFreq = 1200;
  return minFreq + (rule / 255) * (maxFreq - minFreq);
};

export type SoundMode = 'melodic' | 'noise' | 'pulse' | 'ambient';

interface MarketSoundState {
  isEnabled: boolean;
  volume: number;
  mode: SoundMode;
  tickCount: number;
}

export const useMarketSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const lastTickRef = useRef<number>(0);

  const [state, setState] = useState<MarketSoundState>({
    isEnabled: false,
    volume: 0.3,
    mode: 'pulse',
    tickCount: 0,
  });

  const dataFeed = useStore((s) => s.dataFeed);

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (audioContextRef.current) return;

    const ctx = new AudioContext();

    // Create master gain
    const gain = ctx.createGain();
    gain.gain.value = state.volume;

    // Create compressor to prevent clipping
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 30;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    gain.connect(compressor);
    compressor.connect(ctx.destination);

    audioContextRef.current = ctx;
    gainNodeRef.current = gain;
    compressorRef.current = compressor;
  }, [state.volume]);

  // Play a melodic tone based on rule
  const playMelodic = useCallback((rule: number, pressure: number) => {
    const ctx = audioContextRef.current;
    const masterGain = gainNodeRef.current;
    if (!ctx || !masterGain) return;

    const now = ctx.currentTime;
    const freq = ruleToFrequency(rule);

    // Create oscillator
    const osc = ctx.createOscillator();
    const noteGain = ctx.createGain();
    const panner = ctx.createStereoPanner();

    // Set oscillator type based on pressure direction
    osc.type = pressure > 0 ? 'sine' : 'triangle';
    osc.frequency.setValueAtTime(freq, now);

    // Slight detune for thickness
    osc.detune.setValueAtTime((rule % 20) - 10, now);

    // Short envelope for tick sound
    noteGain.gain.setValueAtTime(0, now);
    noteGain.gain.linearRampToValueAtTime(0.15, now + 0.01);
    noteGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

    // Pan based on pressure
    const normalizedPressure = Math.max(-1, Math.min(1, pressure / 100));
    panner.pan.setValueAtTime(normalizedPressure, now);

    osc.connect(noteGain);
    noteGain.connect(panner);
    panner.connect(masterGain);

    osc.start(now);
    osc.stop(now + 0.15);
  }, []);

  // Play noise burst for chaotic mode
  const playNoise = useCallback((rule: number, pressure: number) => {
    const ctx = audioContextRef.current;
    const masterGain = gainNodeRef.current;
    if (!ctx || !masterGain) return;

    const now = ctx.currentTime;

    // Create noise buffer
    const bufferSize = ctx.sampleRate * 0.05; // 50ms
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate noise colored by rule
    const noiseFactor = rule / 255;
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * noiseFactor;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // Filter based on rule
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = ruleToContinuousFrequency(rule);
    filter.Q.value = 5 + (rule / 255) * 15;

    const noteGain = ctx.createGain();
    noteGain.gain.setValueAtTime(0.1, now);
    noteGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

    const panner = ctx.createStereoPanner();
    panner.pan.setValueAtTime(Math.max(-1, Math.min(1, pressure / 100)), now);

    source.connect(filter);
    filter.connect(noteGain);
    noteGain.connect(panner);
    panner.connect(masterGain);

    source.start(now);
  }, []);

  // Play pulse/click for minimal mode
  const playPulse = useCallback((rule: number, pressure: number) => {
    const ctx = audioContextRef.current;
    const masterGain = gainNodeRef.current;
    if (!ctx || !masterGain) return;

    const now = ctx.currentTime;
    const freq = 100 + (rule / 255) * 400;

    const osc = ctx.createOscillator();
    const noteGain = ctx.createGain();
    const panner = ctx.createStereoPanner();

    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, now);

    // Very short click
    noteGain.gain.setValueAtTime(0.08, now);
    noteGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);

    panner.pan.setValueAtTime(Math.max(-1, Math.min(1, pressure / 100)), now);

    osc.connect(noteGain);
    noteGain.connect(panner);
    panner.connect(masterGain);

    osc.start(now);
    osc.stop(now + 0.03);
  }, []);

  // Play ambient drone that evolves
  const playAmbient = useCallback((rule: number, pressure: number) => {
    const ctx = audioContextRef.current;
    const masterGain = gainNodeRef.current;
    if (!ctx || !masterGain) return;

    const now = ctx.currentTime;
    const baseFreq = ruleToFrequency(rule);

    // Create multiple oscillators for rich sound
    const oscs: OscillatorNode[] = [];
    const types: OscillatorType[] = ['sine', 'triangle'];
    const detunes = [0, 5, -5, 12];

    const noteGain = ctx.createGain();
    const panner = ctx.createStereoPanner();

    noteGain.gain.setValueAtTime(0, now);
    noteGain.gain.linearRampToValueAtTime(0.04, now + 0.05);
    noteGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

    panner.pan.setValueAtTime(Math.max(-1, Math.min(1, pressure / 100)), now);

    for (let i = 0; i < 2; i++) {
      const osc = ctx.createOscillator();
      osc.type = types[i];
      osc.frequency.setValueAtTime(baseFreq * (i === 0 ? 1 : 0.5), now);
      osc.detune.setValueAtTime(detunes[i], now);
      osc.connect(noteGain);
      osc.start(now);
      osc.stop(now + 0.4);
      oscs.push(osc);
    }

    noteGain.connect(panner);
    panner.connect(masterGain);
  }, []);

  // Main sonification function
  const sonifyTick = useCallback(
    (lastByteActivity: string[], longPressure: number, shortPressure: number) => {
      if (!state.isEnabled || !audioContextRef.current) return;

      // Convert byte activity to rule number
      const eightBitArray = lastByteActivity.map((b) => (b.includes('+') ? 1 : 0));
      const rule = parseInt(eightBitArray.join(''), 2);

      // Calculate net pressure for panning
      const pressure = longPressure - shortPressure;

      // Rate limiting - don't play more than 50 sounds per second
      const now = performance.now();
      if (now - lastTickRef.current < 20) return;
      lastTickRef.current = now;

      // Play sound based on mode
      switch (state.mode) {
        case 'melodic':
          playMelodic(rule, pressure);
          break;
        case 'noise':
          playNoise(rule, pressure);
          break;
        case 'pulse':
          playPulse(rule, pressure);
          break;
        case 'ambient':
          playAmbient(rule, pressure);
          break;
      }

      setState((prev) => ({ ...prev, tickCount: prev.tickCount + 1 }));
    },
    [state.isEnabled, state.mode, playMelodic, playNoise, playPulse, playAmbient],
  );

  // Listen to data feed changes
  useEffect(() => {
    if (!state.isEnabled || !dataFeed.lastByteActivity) return;

    sonifyTick(dataFeed.lastByteActivity, dataFeed.longPressure || 0, dataFeed.shortPressure || 0);
  }, [dataFeed, state.isEnabled, sonifyTick]);

  // Update volume
  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = state.volume;
    }
  }, [state.volume]);

  // Toggle sound on/off
  const toggle = useCallback(() => {
    if (!state.isEnabled) {
      initAudio();
      // Resume audio context if suspended (browser autoplay policy)
      audioContextRef.current?.resume();
    }
    setState((prev) => ({ ...prev, isEnabled: !prev.isEnabled }));
  }, [state.isEnabled, initAudio]);

  // Set volume (0-1)
  const setVolume = useCallback((volume: number) => {
    setState((prev) => ({ ...prev, volume: Math.max(0, Math.min(1, volume)) }));
  }, []);

  // Set sound mode
  const setMode = useCallback((mode: SoundMode) => {
    setState((prev) => ({ ...prev, mode }));
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  return {
    isEnabled: state.isEnabled,
    volume: state.volume,
    mode: state.mode,
    tickCount: state.tickCount,
    toggle,
    setVolume,
    setMode,
  };
};
